import type { QueryClient } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import type { components } from '@/api/schema'
import type { EventsParams } from '@/features/market/api/marketApi'
import { pageTotal } from '@/features/market/api/marketMappers'
import { formatBsWithAd } from '@/lib/bsDate'
import {
  EVENTS_SEARCH_MAX_LENGTH,
  eventRowSchema,
  type EventRow,
  type EventsQuery,
} from '@/schemas/market-events'
import { lenientList } from '@/schemas/common'
import { tablePageSchema, type TablePage } from '@/schemas/table'

const eventsPageSchema = tablePageSchema(eventRowSchema)
// One notice with a kind this build does not know, or a blank title, costs that row — not the page.
const eventRowsSchema = lenientList(eventRowSchema, 'event')

// Always the plain "BS (AD)" date, never a relative label like "Closes Thu":
// `MarketEvent` (schema.d.ts) carries only `type`/`date`/`company`/
// `company_name`/`title`/`detail` — no status field a "closing soon" state
// could read.
const toEventRow = (row: components['schemas']['MarketEvent']) => ({
  id: `${row.type}-${row.date}-${row.company ?? row.title}`,
  title: row.title,
  body: row.detail,
  dateLabel: formatBsWithAd(row.date),
  date: row.date,
  company: row.company,
  companyName: row.company_name,
  kind: row.type,
})

/**
 * The screen's filters as mm-be's: `query→q`, `date→start`, `dateTo→end`.
 *
 * `end` rides only with a `start`, as the date chip's range always does, and a range typed into
 * the URL backwards is put the right way round — mm-be answers `start > end` with a 400, and a
 * hand-edited link should not turn a filter into an error page.
 */
const toParams = (query: EventsQuery): EventsParams => {
  const { date, dateTo } = query
  const [start, end] =
    date !== undefined && dateTo !== undefined && dateTo < date ? [dateTo, date] : [date, dateTo]
  return {
    page: query.page,
    page_size: query.pageSize,
    ...(query.type === undefined ? {} : { type: query.type }),
    ...(query.query === undefined ? {} : { q: query.query.slice(0, EVENTS_SEARCH_MAX_LENGTH) }),
    ...(start === undefined ? {} : { start }),
    ...(start === undefined || end === undefined ? {} : { end }),
  }
}

export const eventsService = {
  /**
   * One server page of the calendar. Search, type and dates are all mm-be's to apply (its
   * `total` counts what matches them), so a page click reads one request, not the whole set.
   */
  table: async (query: EventsQuery, client?: QueryClient): Promise<TablePage<EventRow>> => {
    const page = await marketEndpoints(client).events(toParams(query))
    return eventsPageSchema.parse({
      rows: eventRowsSchema.parse(page.rows.map(toEventRow)),
      page: query.page,
      pageSize: query.pageSize,
      total: pageTotal(page.meta, page.rows.length),
    })
  },
}
